import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle, TrafficCone, Sofa, MapPinned, Building2, Truck, Quote, Shield, Trophy, Users2, Sparkles } from "lucide-react";
import { SITE, PROJECTS, SERVICES, STATS, TESTIMONIALS, WHY_US } from "@/data/site";

const ICONS = { TrafficCone, Sofa, MapPinned, Building2, Truck };
const WHY_ICONS = [Shield, Sparkles, Trophy, Users2];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/home.jpeg"
            alt="SkyRise flagship residential tower" className="w-full h-full object-cover"
            style={{ objectPosition: "center 20%" }}/>
          <div className="absolute inset-0 hero-mask"></div>
        </div>
        <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-10 pt-40 pb-24 text-white">
          <div className="max-w-3xl rise">
            <div className="eyebrow mb-6 flex items-center gap-3">
              <span className="divider-gold"/> SkyRise Estate & Construction · Vastu-First Builders
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight">
              Building <span className="text-[#C5A059] italic font-medium">Trust</span>,<br/>
              Rising <span className="text-[#C5A059] italic font-medium">Higher</span>.
            </h1>
            <p className="mt-8 max-w-xl text-white/80 text-lg leading-relaxed">
              A full-spectrum real estate & construction partner — every home, office and tower we deliver is
              <span className="text-[#C5A059]"> oriented, planned and finished in strict accordance with Vastu Shastra</span>.
              Because a house that is aligned becomes a home that rises.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/projects" className="btn-gold" data-testid="hero-cta-projects">
                Explore Projects <ArrowRight size={16}/>
              </Link>
              <a href={SITE.siteVisitHref("SkyRise Projects")} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid="hero-cta-visit">
                <MessageCircle size={16}/> Book Site Visit
              </a>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-3xl">
              {STATS.map(s => (
                <div key={s.label} className="border-t border-white/15 pt-4" data-testid={`hero-stat-${s.label.replace(/\s+/g,'-').toLowerCase()}`}>
                  <div className="font-serif-display text-4xl md:text-5xl text-[#C5A059]">{s.value}</div>
                  <div className="text-xs md:text-sm uppercase tracking-[0.2em] text-white/70 mt-2">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ticker strip */}
      <section className="bg-[#0F1E3A] text-white/80 border-y border-white/10 py-4 overflow-hidden">
        <div className="marquee-track gap-14 whitespace-nowrap text-sm uppercase tracking-[0.28em]">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-14">
              <span className="text-[#C5A059]">●</span> Residential
              <span className="text-[#C5A059]">●</span> Commercial
              <span className="text-[#C5A059]">●</span> Infrastructure
              <span className="text-[#C5A059]">●</span> Hospitality
              <span className="text-[#C5A059]">●</span> Retail
              <span className="text-[#C5A059]">●</span> Mixed-Use
              <span className="text-[#C5A059]">●</span> Interior Design
              <span className="text-[#C5A059]">●</span> Materials Trading
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT / INTRO */}
      <section className="py-24 md:py-32 bg-[#F8F7F5]" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5">
            <div className="relative">
              <img
                src="/images/about.jpeg"
                alt="SkyRise office interior — Vastu planned workspace"
                className="w-full h-[520px] object-cover"
                style={{ objectPosition: "center 15%" }}
              />
              <div className="absolute -bottom-8 -right-6 bg-[#0B162C] text-white px-8 py-6 hidden md:block">
                <div className="font-serif-display text-4xl text-[#C5A059]">5+</div>
                <div className="text-xs uppercase tracking-[0.24em] text-white/70">Years of Craft</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="eyebrow mb-5">About SkyRise</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#0B162C]">
              A single, accountable partner for every square foot you invest in.
            </h2>
            <p className="mt-6 text-[17px] leading-[1.85] text-[#5C6B7B] max-w-xl">
              From land acquisition to key handover — we build homes, offices, industrial parks and infrastructure with
              uncompromising quality. Our five practice areas work as one team, so you never have to coordinate between
              vendors, chase deadlines or worry about the fine print.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {WHY_US.map((w, i) => {
                const Ic = WHY_ICONS[i];
                return (
                  <div key={w.title} className="flex gap-4" data-testid={`why-us-${i}`}>
                    <div className="w-11 h-11 grid place-items-center bg-[#0B162C] text-[#C5A059] shrink-0">
                      <Ic size={18}/>
                    </div>
                    <div>
                      <div className="font-serif-display text-xl text-[#0B162C]">{w.title}</div>
                      <p className="text-sm text-[#5C6B7B] mt-1 leading-relaxed">{w.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10">
              <Link to="/about" className="btn-outline-navy" data-testid="home-about-cta">
                About Our Company <ArrowRight size={16}/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-white" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-4">What We Do</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#0B162C] leading-[1.05] max-w-2xl">
                Five practices. <span className="italic text-[#C5A059]">One</span> obsession — delivery.
              </h2>
            </div>
            <Link to="/services" className="btn-outline-navy self-start md:self-auto" data-testid="services-view-all">
              View All Services <ArrowRight size={16}/>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, idx) => {
              const Ic = ICONS[s.icon];
              return (
                <Link
                  key={s.id}
                  to={`/services#${s.id}`}
                  data-testid={`home-service-card-${s.id}`}
                  className={`group border border-[#E5E7EB] p-8 bg-white hover:bg-[#0B162C] hover:text-white transition-colors duration-500 ${idx === 0 ? "lg:col-span-1" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 grid place-items-center bg-[#F8F7F5] group-hover:bg-[#C5A059] transition-colors">
                      <Ic size={20} className="text-[#0B162C] group-hover:text-white transition-colors"/>
                    </div>
                    <span className="text-xs uppercase tracking-[0.24em] text-[#C5A059]">0{idx+1}</span>
                  </div>
                  <h3 className="mt-8 font-serif-display text-2xl md:text-[26px] leading-tight">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#5C6B7B] group-hover:text-white/75 transition-colors">{s.short}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#C5A059] group-hover:gap-3 transition-all">
                    Learn more <ArrowRight size={14}/>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS BENTO */}
      <section className="py-24 md:py-32 bg-[#F8F7F5]" data-testid="home-projects">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-4">Selected Work</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#0B162C] leading-[1.05] max-w-2xl">
                Projects that <span className="italic text-[#C5A059]">rise</span> above expectations.
              </h2>
            </div>
            <Link to="/projects" className="btn-outline-navy self-start md:self-auto" data-testid="projects-view-all">
              All Projects <ArrowRight size={16}/>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5 auto-rows-[220px]">
            {PROJECTS.slice(0, 6).map((p, i) => {
              const layout = [
                "md:col-span-2 md:row-span-2",
                "md:col-span-2 md:row-span-1",
                "md:col-span-1 md:row-span-1",
                "md:col-span-1 md:row-span-1",
                "md:col-span-2 md:row-span-1",
                "md:col-span-2 md:row-span-1",
              ][i];
              return (
                <div key={p.title} className={`project-card ${layout} group`} data-testid={`home-project-${i}`}>
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover"/>
                  <div className="absolute inset-0 veil"></div>
                  <div className="absolute top-4 left-4 flex gap-2 z-10">
                    <span className="text-[10px] uppercase tracking-[0.28em] bg-[#C5A059] text-white px-2 py-1">{p.category}</span>
                    <span className="text-[10px] uppercase tracking-[0.28em] bg-[#0B162C]/85 text-[#C5A059] px-2 py-1 border border-[#C5A059]/40 backdrop-blur-sm">Vastu</span>
                  </div>
                  <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                    <div className="font-serif-display text-2xl md:text-3xl leading-tight">{p.title}</div>
                    <a
                      href={SITE.siteVisitHref(p.title)}
                      target="_blank" rel="noreferrer"
                      data-testid={`home-project-visit-${i}`}
                      className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#C5A059] opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MessageCircle size={12}/> Book Site Visit
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-white" data-testid="home-testimonials">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow mb-4">Trusted by</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#0B162C] leading-[1.05]">
              Voices from clients who built with us.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <blockquote key={i} className="border border-[#E5E7EB] p-8 flex flex-col gap-6 bg-[#F8F7F5]" data-testid={`testimonial-${i}`}>
                <Quote size={22} className="text-[#C5A059]"/>
                <p className="text-[#1A2530] leading-relaxed text-[17px]">“{t.quote}”</p>
                <footer className="mt-auto">
                  <div className="font-serif-display text-lg text-[#0B162C]">{t.name}</div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#5C6B7B] mt-1">{t.role}</div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-28 bg-[#0B162C] text-white overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0 opacity-25">
          <img src="https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow mb-5">Start Your Project</div>
            <h3 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              One energy from our name. <span className="italic text-[#C5A059]">One</span> from our number. Together — double leadership.
            </h3>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
            <a href={SITE.telHref} className="btn-gold w-full md:w-auto justify-center" data-testid="cta-call-now">
              <Phone size={16}/> Call {SITE.phone}
            </a>
            <a href={SITE.whatsappHref} target="_blank" rel="noreferrer" className="btn-outline-gold w-full md:w-auto justify-center" data-testid="cta-whatsapp">
              <MessageCircle size={16}/> WhatsApp Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
